import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

const STATUS_STYLES = {
  RUNNING: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  STARTING: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  STOPPED: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  CRASHED: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  STATIC: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  node: "bg-violet-500/10 text-violet-300 border-violet-500/20",
  static: "bg-sky-500/10 text-sky-300 border-sky-500/20",
};

export function StatusBadge({ status, className }) {
  const dot = { RUNNING: "bg-emerald-400", STARTING: "bg-amber-400", CRASHED: "bg-rose-400", STOPPED: "bg-zinc-500" }[status];
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={cn("inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-mono uppercase tracking-wide",
        STATUS_STYLES[status] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20", className)}
    >
      {dot && <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />}
      {status}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions, breadcrumb }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 border-b border-zinc-800 pb-5 mb-6">
      <div>
        {breadcrumb && <div className="text-xs font-mono text-zinc-500 mb-1">{breadcrumb}</div>}
        <h1 className="font-head text-2xl font-semibold tracking-tight text-zinc-50" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-400 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 text-sm text-zinc-500 py-12 justify-center" data-testid="loading">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}…
    </div>
  );
}

export function EmptyState({ icon: Icon, title, children }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-zinc-800 py-16 text-center">
      {Icon && <Icon className="h-8 w-8 text-zinc-600 mb-3" />}
      <p className="font-head text-lg text-zinc-300">{title}</p>
      {children && <div className="text-sm text-zinc-500 mt-1">{children}</div>}
    </div>
  );
}

export function Stat({ label, value, unit }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-2xl text-zinc-50 mt-1">{value}<span className="text-sm text-zinc-500 ml-1">{unit}</span></div>
    </div>
  );
}

export function fmtBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i ? 1 : 0)} ${u[i]}`;
}

export function fmtUptime(s) {
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}
